
import { defineComponent } from 'vue';
import PreScreenForm from '../components/PreScreenForm.vue';
import FormTextBox from '../components/FormTextBox.vue';
import ValidationErrors from '../components/ValidationErrors.vue';
import { Form } from 'vee-validate';
import * as yup from 'yup';
import axios from 'axios';
import { add, parseJSON } from 'date-fns'
import debounce from '../utils/debounce'
import { generateValidationSummary, handleResponse, scrollTop, ValidationError } from '../utils/api';
import { challenge } from '@/utils/captcha';

const localStorageKeys = {
    caseState: 'cspPrescreen-caseState',
    contactInfo: 'cspPrescreen-contactInfo',
    prescreen: 'cspPrescreen-prescreen',
};

export default defineComponent({
    name: 'PreScreenPublic',
    components: {
        PreScreenForm,
        FormTextBox,
        Form,
        ValidationErrors
    },
    setup() {
        const validationSchema = yup.object({
            email: yup.string().required().email(),
            firstName: yup.string().required(),
            lastName: yup.string().required(),
            dateOfBirth: yup.string().required()
                .matches(/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/, 'validation.date')
                .test('date-range', 'validation.dateOfBirth', function (value) {
                    if (!value) return true; 
                    const inputDate = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return inputDate >= new Date('1/1/1900') && inputDate <= today;
                }),
            phoneNumber: yup.string().required().matches(/^\(?([0-9]{3})\)?[-.]?([0-9]{3})[-.]?([0-9]{4})$/, 'validation.phone'),
            zipCode: yup.string().required().matches(/\d{5}/, 'validation.zip'),
            streetAddress: yup.string().required(),
        });

        return { validationSchema}
    },
    created() {
        this.loadCaseState();
    },
    data() {
        return {
            step: 1,
            error: '',
            validationErrors: [] as ValidationError[],
            allowRetry: false,
            isSubmitting: false,
            case: {
                id: 0,
                guid: ''
            },
            contactInfo: {
                email: '',
                firstName: '',
                lastName: '',
                dateOfBirth: '',
                phoneNumber: '', 
                address: '',
                aptNumber: '',
                zipCode: '',
                city: '',
                state: '',
                county: ''
            },
            initialPreScreenData: {} as any
        }
    },
    methods: {
        async submitContactInfo() {
            this.isSubmitting = true;
            this.resetValidation();

            try {
                // Captcha challenge
                const captcha = await challenge('submitContactInfo');

                const res = handleResponse(await axios.post('/WebServices/CSP/PreScreenV2Service.asmx/SubmitPreScreenContactInfo', { 
                    contactInfo: this.contactInfo,
                    captchaToken: captcha
                }), true);

                if(res?.success) {
                    const {data} = res;
                    this.saveCaseState(data.caseId, data.caseGuid)
                    this.step = 2;
                    scrollTop();
                    return;
                } else if(res.invalid) {
                    this.validationErrors = res.validationErrors! as ValidationError[];
                    this.allowRetry = true;
                    scrollTop();
                } else {
                    this.error = res.error!;
                    this.allowRetry = true;
                }
            } catch(e) {
                console.log(e);
                this.error = 'errors.unknown';
            } finally {
                this.isSubmitting = false;
            }
        },

        // eslint-disable-next-line no-unused-vars
        handleIneligible(message: string, prescreenData: any) {
            // Handle ineligible submits normally
            this.submitPreScreen(prescreenData);
        },

        async submitPreScreen(preScreenData: any) {
            console.log(preScreenData);
            this.isSubmitting = true;
            this.resetValidation();

            try {
                // Captcha challenge
                const captcha = await challenge('submitPreScreen');

                const res = handleResponse(await axios.post('/WebServices/CSP/PreScreenV2Service.asmx/SubmitPreScreen', { 
                    caseId: this.case.id,
                    caseGuid: this.case.guid,
                    captchaToken: captcha,
                    preScreen: preScreenData
                }), true);

                console.log(res);

                if(res?.success) {
                    this.step = 3;
                    this.clearCaseState();
                    return;
                } else if(res.invalid) {
                    this.validationErrors = res.validationErrors! as ValidationError[];
                    this.allowRetry = true;
                    scrollTop();
                } else {
                    this.error = res.error!;
                }
            } catch(e) {
                console.log(e);
                this.error = 'errors.unknown';
            } finally {
                this.isSubmitting = false;
            }

        },

        validateZipCode: debounce(async function (this: any, zipCode: string ) {
            try {
                const res = handleResponse(await axios.post('/WebServices/CSP/PreScreenV2Service.asmx/LookupZipCode', { zipInput: zipCode }));
                const {data} = res;
                if(res?.success) {
                    if(data) {
                        this.contactInfo.city = data.City;
                        if(data.County) {
                            this.contactInfo.county = data.County.Name;
                        }
                        if(data.State) {
                            this.contactInfo.state = data.State.Name;
                        }
                    } else {
                        this.contactInfo.city = '';
                        this.contactInfo.county = '';
                        this.contactInfo.state = '';
                    }
                } 
            } catch(e) {
                console.log(e);
            }
        }, 500),

        savePreScreenState(prescreenData: any) {
            if(this.step > 1 && this.case.id) {
                localStorage.setItem(localStorageKeys.prescreen, JSON.stringify(prescreenData));
            }
        },

        saveCaseState(caseId: number, caseGuid: string) {
            const caseState = {
                caseId: caseId,
                caseGuid: caseGuid,
                expires: add(Date.now(), { months: 1 })
            }
            localStorage.setItem(localStorageKeys.caseState, JSON.stringify(caseState));
            localStorage.setItem(localStorageKeys.contactInfo, JSON.stringify(this.contactInfo));
            this.case.id = caseId;
            this.case.guid = caseGuid;
        },

        loadCaseState() {
            try {
                const caseState = localStorage.getItem(localStorageKeys.caseState);
                if(caseState) {
                    const caseStateData = JSON.parse(caseState);
                    const { caseId, caseGuid, expires } = caseStateData || {};
                    if(caseId && caseGuid && parseJSON(expires) > new Date()) {
                        this.case.id = caseId;
                        this.case.guid = caseGuid;
                        
                        const contactStr = localStorage.getItem(localStorageKeys.contactInfo);
                        if(contactStr) {
                            this.contactInfo = JSON.parse(contactStr);
                        }

                        const prescreenStr = localStorage.getItem(localStorageKeys.prescreen);
                        if(prescreenStr) {
                            this.initialPreScreenData = JSON.parse(prescreenStr);
                        }

                        this.step = 2;
                        return;
                    }
                }
            } catch (e) {
                console.log(e);
            }
            this.clearCaseState();
        },

        clearCaseState() {
            localStorage.removeItem(localStorageKeys.caseState);
            localStorage.removeItem(localStorageKeys.contactInfo);
            localStorage.removeItem(localStorageKeys.prescreen);
        },
        handleInvalid(data: any) {
            const { errors } = data;
            this.resetValidation();
            this.validationErrors = generateValidationSummary(errors);
            scrollTop();
        },
        resetForm() {
            this.clearCaseState();
            window.location.reload();
        },
        resetValidation() {
            this.validationErrors = [] as ValidationError[];
            this.error = '';
            this.allowRetry = false;
        }        
    }
});
